import React from "react";
import { Select, Tag, Button } from "antd";
import { Eye } from "lucide-react";
import { useQueryStates, parseAsInteger, parseAsString } from "nuqs";
import dayjs from "dayjs";
import DataTable from "../../components/DataTable";
import { useAdminOrderList } from "../../../hooks/api/order/hooks";
import { IAdminOrder } from "../../../types/api/order";
import OrderDetailModal from "./OrderDetailModal";
import { useModalStore } from "../../../store/slices/modal";
import { MODAL_TYPES } from "../../../types/modal-types";

const OrderList: React.FC = () => {
  // Single qData state object managed by nuqs useQueryStates
  const [qData, setQData] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    limit: parseAsInteger.withDefault(10),
    search: parseAsString.withDefault(""),
    paymentStatus: parseAsString.withDefault(""),
    orderStatus: parseAsString.withDefault(""),
    startDate: parseAsString.withDefault(""),
    endDate: parseAsString.withDefault(""),
  });

  const queryParams = {
    page: qData.page,
    limit: qData.limit,
    search: qData.search || undefined,
    paymentStatus: qData.paymentStatus || undefined,
    orderStatus: qData.orderStatus || undefined,
    startDate: qData.startDate || undefined,
    endDate: qData.endDate || undefined,
  };

  const { type, data: selectedOrder, open, close } = useModalStore();

  const { data, isLoading, isError } = useAdminOrderList(queryParams);

  const handleViewDetails = (record: IAdminOrder) => {
    open(MODAL_TYPES.VIEW_ORDER_DETAILS, record);
  };

  const columns = [
    {
      title: "Order ID",
      dataIndex: "orderNumber",
      key: "orderNumber",
      render: (text: string, record: IAdminOrder) => (
        <Button
          type="link"
          className="p-0 font-semibold text-slate-800 hover:text-blue-600"
          onClick={() => handleViewDetails(record)}
        >
          {text}
        </Button>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      render: (record: IAdminOrder) => (
        <div>
          <div className="font-medium text-slate-800">
            {record.shippingAddress?.fullName || record.billingAddress?.fullName || "N/A"}
          </div>
          <div className="text-xs text-slate-400">
            {record.shippingAddress?.email || record.billingAddress?.email || "-"}
          </div>
        </div>
      ),
    },
    {
      title: "Total Amount",
      key: "price",
      render: (record: IAdminOrder) => (
        <span className="font-semibold text-slate-800">
          £{record.price?.totalAmount?.toLocaleString("en-GB")}
        </span>
      ),
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (status: string) => {
        let color = "orange";
        if (status === "PAID") color = "green";
        if (status === "FAILED" /* || status === "REFUNDED" */) color = "red";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Order Status",
      dataIndex: "orderStatus",
      key: "orderStatus",
      render: (status: string) => {
        let color = "blue";
        if (status === "DELIVERED") color = "green";
        if (status === "CANCELLED" /* || status === "REFUNDED" */) color = "volcano";
        if (status === "PENDING") color = "gold";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date?: string) => (date ? new Date(date).toLocaleDateString() : "-"),
    },
    {
      title: "Action",
      key: "action",
      render: (record: IAdminOrder) => (
        <Button
          type="text"
          icon={<Eye className="w-4 h-4 text-blue-600" />}
          onClick={() => handleViewDetails(record)}
        >
          Details
        </Button>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      <DataTable
        title="Order Management"
        dataSource={data?.data || []}
        columns={columns}
        loading={isLoading}
        isError={isError}
        rowKey="_id"
        searchProps={{
          searchValue: qData.search,
          onSearchChange: (v) => setQData({ search: v, page: 1 }),
          placeholder: "Search Order ID, Customer Name or Email...",
          showStatusFilter: false,
          showDateRangeFilter: true,
          dateRangeValue: [
            qData.startDate ? dayjs(qData.startDate) : null,
            qData.endDate ? dayjs(qData.endDate) : null,
          ],
          onDateRangeChange: (_, dateStrings) => {
            setQData({
              startDate: dateStrings[0] || "",
              endDate: dateStrings[1] || "",
              page: 1,
            });
          },
          children: (
            <div className="flex gap-2">
              <Select
                placeholder="Payment Status"
                value={qData.paymentStatus || undefined}
                onChange={(val) => setQData({ paymentStatus: val || "", page: 1 })}
                allowClear
                className="w-40"
                options={[
                  { value: "PENDING", label: "Payment: Pending" },
                  { value: "PAID", label: "Payment: Paid" },
                  { value: "FAILED", label: "Payment: Failed" },
                  // { value: "REFUNDED", label: "Payment: Refunded" },
                ]}
              />

              <Select
                placeholder="Order Status"
                value={qData.orderStatus || undefined}
                onChange={(val) => setQData({ orderStatus: val || "", page: 1 })}
                allowClear
                className="w-40"
                options={[
                  { value: "PENDING", label: "Pending" },
                  { value: "CONFIRMED", label: "Confirmed" },
                  { value: "PROCESSING", label: "Processing" },
                  { value: "DISPATCHED", label: "Dispatched" },
                  { value: "SHIPPED", label: "Shipped" },
                  { value: "IN_TRANSIT", label: "In Transit" },
                  { value: "DELIVERED", label: "Delivered" },
                  { value: "CANCELLED", label: "Cancelled" },
                ]}
              />
            </div>
          ),
        }}
        paginationProps={{
          page: qData.page,
          limit: qData.limit,
          total: data?.extra?.total || 0,
          onChange: (newPage, newLimit) =>
            setQData(
              newLimit !== qData.limit ? { page: 1, limit: newLimit } : { page: newPage },
            ),
        }}
      />

      <OrderDetailModal
        isOpen={type === MODAL_TYPES.VIEW_ORDER_DETAILS}
        onClose={close}
        orderId={selectedOrder?._id || selectedOrder}
      />
    </div>
  );
};

export default OrderList;
